import React, { useState, useRef } from "react";
import { Icon } from "@iconify/react";

/**
 * App Store Screenshot Frame Generator v5
 *
 * Manus-inspired minimal design: pure white background, centered phone,
 * bold headline at top, faded accent word behind. No floating cards,
 * no pills, no gradients — just clean typography and a phone.
 */

const DEVICE_SPECS = {
  iphone: { width: 1320, height: 2868, name: 'iPhone 6.7"' },
  ipad: { width: 2064, height: 2752, name: 'iPad 12.9"' },
};

type DeviceType = keyof typeof DEVICE_SPECS;
type Lang = "zh" | "en";

// --- Reusable elements ---

const PhoneFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.15)) drop-shadow(0 10px 20px rgba(0,0,0,0.08))" }}>
    <div style={{ borderRadius: 56, padding: 11, background: "linear-gradient(155deg, #2c2c2c 0%, #1a1a1a 40%, #111 100%)", position: "relative" }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: 56, background: "linear-gradient(155deg, rgba(255,255,255,0.12) 0%, transparent 40%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", left: -2, top: 110, width: 3, height: 30, background: "#2a2a2a", borderRadius: "2px 0 0 2px" }} />
      <div style={{ position: "absolute", left: -2, top: 160, width: 3, height: 55, background: "#2a2a2a", borderRadius: "2px 0 0 2px" }} />
      <div style={{ position: "absolute", left: -2, top: 230, width: 3, height: 55, background: "#2a2a2a", borderRadius: "2px 0 0 2px" }} />
      <div style={{ position: "absolute", right: -2, top: 180, width: 3, height: 75, background: "#2a2a2a", borderRadius: "0 2px 2px 0" }} />
      <div style={{ borderRadius: 46, overflow: "hidden", background: "#000", position: "relative" }}>
        <div style={{ position: "absolute", top: 11, left: "50%", transform: "translateX(-50%)", width: 120, height: 34, background: "#000", borderRadius: 100, zIndex: 20 }} />
        <div style={{ width: 390, height: 844 }}>{children}</div>
      </div>
    </div>
  </div>
);

const TabletFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.15)) drop-shadow(0 10px 20px rgba(0,0,0,0.08))" }}>
    <div style={{ borderRadius: 28, padding: 10, background: "linear-gradient(155deg, #2c2c2c 0%, #1a1a1a 40%, #111 100%)", position: "relative" }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: 28, background: "linear-gradient(155deg, rgba(255,255,255,0.10) 0%, transparent 40%)", pointerEvents: "none" }} />
      <div style={{ borderRadius: 20, overflow: "hidden", background: "#000", position: "relative" }}>
{/* no camera dot */}
        <div style={{ width: 480, height: 640 }}>{children}</div>
      </div>
    </div>
  </div>
);

const INNER_SIZE = { iphone: { w: 390, h: 844 }, ipad: { w: 480, h: 640 } };

const UploadZone: React.FC<{ label: string; onUpload: (d: string) => void; device?: DeviceType }> = ({ label, onUpload, device = "iphone" }) => {
  const ref = useRef<HTMLInputElement>(null);
  const { w, h } = INNER_SIZE[device];
  const handle = (f: File) => { const r = new FileReader(); r.onload = (e) => { if (e.target?.result) onUpload(e.target.result as string); }; r.readAsDataURL(f); };
  return (
    <div style={{ width: w, height: h, background: "#f7f7f7", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); ref.current?.click(); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files[0]; if (f?.type.startsWith("image/")) handle(f); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <Icon icon="lucide:image-plus" style={{ fontSize: 36, color: "#ccc", marginBottom: 12 }} />
      <p style={{ fontSize: 14, color: "#aaa", fontWeight: 500 }}>{label}</p>
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handle(f); e.target.value = ""; }} />
    </div>
  );
};

// --- Frame data ---

interface FrameConfig {
  accent: string;
  accentSize?: number; // override default 280
  en: { headline: string; subtitle: string };
  zh: { headline: string; subtitle: string };
  label: string;
}

const FRAME_DATA: FrameConfig[] = [
  {
    accent: "REC",
    en: { headline: "Record.\nTranscribe. Done.", subtitle: "Real-time transcription in 60 languages" },
    zh: { headline: "录音。转写。\n即刻完成。", subtitle: "60种语言实时转录" },
    label: "Recording",
  },
  {
    accent: "AI",
    en: { headline: "AI reads it.\nSo you don't\nhave to.", subtitle: "Summaries with source citations" },
    zh: { headline: "AI帮你读。\n省时省力。", subtitle: "结构化总结，带引用来源" },
    label: "AI Summary",
  },
  {
    accent: "ALL",
    en: { headline: "Everything.\nOne place.", subtitle: "Meetings, docs, videos, voice memos" },
    zh: { headline: "所有内容。\n一个地方。", subtitle: "会议、文档、视频、语音备忘" },
    label: "Home Screen",
  },
  {
    accent: "60+",
    en: { headline: "Translate\nas you speak.", subtitle: "Dual-language transcript in real-time" },
    zh: { headline: "边说边翻。\n实时双语。", subtitle: "实时双语转录" },
    label: "Translation",
  },
  {
    accent: "▶",
    accentSize: 220,
    en: { headline: "Follow every\nword.", subtitle: "Word-by-word playback & highlighting" },
    zh: { headline: "逐词跟读。\n永不掉队。", subtitle: "逐词播放与高亮" },
    label: "YouTube Playback",
  },
  {
    accent: "Q&A",
    en: { headline: "Ask anything.\nAI knows.", subtitle: "AI chat grounded in your content" },
    zh: { headline: "问任何问题。\nAI帮你找答案。", subtitle: "AI对话，引用你的笔记" },
    label: "AI Chat",
  },
];

const LABELS_EN = FRAME_DATA.map(f => f.label);
const LABELS_ZH = ["录音转写", "AI总结", "主页", "实时翻译", "YouTube播放", "AI对话"];

// --- Minimal frame component ---

const MinimalFrame: React.FC<{
  s: number; d: DeviceType; l: Lang;
  img: string | null; onUp: (d: string) => void;
  data: FrameConfig;
}> = ({ s, d, l, img, onUp, data }) => {
  const W = DEVICE_SPECS[d].width * s;
  const H = DEVICE_SPECS[d].height * s;
  const ps = d === "iphone" ? 2.3 * s : 2.8 * s;
  const content = l === "zh" ? data.zh : data.en;
  const accentFontSize = (data.accentSize || 280) * s;

  return (
    <div style={{ width: W, height: H, background: "#ffffff", position: "relative", overflow: "hidden" }}>
      {/* Faded accent word — behind everything */}
      <div style={{
        position: "absolute", top: H * 0.12, left: 0, right: 0,
        textAlign: "center", pointerEvents: "none",
        fontSize: accentFontSize, fontWeight: 900, color: "rgba(0,0,0,0.05)",
        letterSpacing: -8 * s, lineHeight: 0.85,
        fontFamily: '-apple-system, "SF Pro Display", "Helvetica Neue", sans-serif',
      }}>
        {data.accent}
      </div>

      {/* Headline — top center */}
      <div style={{ position: "absolute", top: H * 0.04, left: 0, right: 0, textAlign: "center", zIndex: 1 }}>
        <h1 style={{
          fontSize: 96 * s, fontWeight: 900, color: "#000", lineHeight: 1.05,
          letterSpacing: -2 * s, whiteSpace: "pre-line", margin: 0,
          fontFamily: '-apple-system, "SF Pro Display", "Helvetica Neue", sans-serif',
        }}>
          {content.headline}
        </h1>
        <p style={{ fontSize: 36 * s, color: "#888", fontWeight: 500, marginTop: 14 * s }}>
          {content.subtitle}
        </p>
      </div>

      {/* Device — centered, below headline */}
      <div style={{
        position: "absolute", top: H * 0.22, left: "50%",
        transform: `translateX(-50%) scale(${ps})`,
        transformOrigin: "top center",
      }}>
        {d === "ipad" ? (
          <TabletFrame>
            {img ? (
              <img src={img} alt="" style={{ width: 480, height: 640, objectFit: "cover" }} draggable={false} />
            ) : (
              <UploadZone label={data.label} onUpload={onUp} device="ipad" />
            )}
          </TabletFrame>
        ) : (
          <PhoneFrame>
            {img ? (
              <img src={img} alt="" style={{ width: 390, height: 844, objectFit: "cover" }} draggable={false} />
            ) : (
              <UploadZone label={data.label} onUpload={onUp} />
            )}
          </PhoneFrame>
        )}
      </div>
    </div>
  );
};

// Pre-loaded screenshot paths (from public/internal/screenshots/)
const DEFAULT_SCREENSHOTS: Record<string, string> = {
  "en-iphone-0": "/internal/screenshots/frame1-recording.png",
  "en-iphone-1": "/internal/screenshots/frame2-summary.png",
  "en-iphone-2": "/internal/screenshots/frame3-home.png",
  "en-iphone-3": "/internal/screenshots/frame4-translation.png",
  "en-iphone-4": "/internal/screenshots/frame5-youtube.png",
  "en-iphone-5": "/internal/screenshots/frame6-chat.png",
  "zh-iphone-0": "/internal/screenshots/zh-frame1-recording.png",
  "zh-iphone-1": "/internal/screenshots/zh-frame2-summary.png",
  "zh-iphone-2": "/internal/screenshots/zh-frame3-home.png",
  "zh-iphone-3": "/internal/screenshots/zh-frame4-translation.png",
  "zh-iphone-4": "/internal/screenshots/zh-frame5-youtube.png",
  "zh-iphone-5": "/internal/screenshots/zh-frame6-chat.png",
  "en-ipad-0": "/internal/screenshots/en-ipad-frame1-recording.png",
  "en-ipad-1": "/internal/screenshots/en-ipad-frame2-summary.png",
  "en-ipad-2": "/internal/screenshots/en-ipad-frame3-home.png",
  "en-ipad-3": "/internal/screenshots/en-ipad-frame4-translation.png",
  "en-ipad-4": "/internal/screenshots/en-ipad-frame5-youtube.png",
  "en-ipad-5": "/internal/screenshots/en-ipad-frame6-chat.png",
  "zh-ipad-0": "/internal/screenshots/zh-ipad-frame1-recording.png",
  "zh-ipad-1": "/internal/screenshots/zh-ipad-frame2-summary.png",
  "zh-ipad-2": "/internal/screenshots/zh-ipad-frame3-home.png",
  "zh-ipad-3": "/internal/screenshots/zh-ipad-frame4-translation.png",
  "zh-ipad-4": "/internal/screenshots/zh-ipad-frame5-youtube.png",
  "zh-ipad-5": "/internal/screenshots/zh-ipad-frame6-chat.png",
};

// --- Main ---
const AppStoreScreenshots: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [gridScale, setGridScale] = useState(0.15);
  const [deviceType, setDeviceType] = useState<DeviceType>("iphone");
  const [lang, setLang] = useState<Lang>("en");
  const [uploadedImages, setUploadedImages] = useState<Record<string, string>>({});

  const expandedScale = 0.38;
  const device = DEVICE_SPECS[deviceType];
  const imageKey = (i: number) => `${lang}-${deviceType}-${i}`;
  const getImage = (i: number) => uploadedImages[imageKey(i)] || DEFAULT_SCREENSHOTS[imageKey(i)] || null;

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a" }}>
      {/* Header */}
      <div style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(10,10,10,0.88)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth: 1800, margin: "0 auto", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 600, color: "#fff", letterSpacing: -0.3 }}>Frame Generator</h1>
            <p style={{ color: "#555", fontSize: 13, marginTop: 2 }}>
              {selectedIndex !== null ? `Screenshot ${selectedIndex+1} of ${FRAME_DATA.length}` : `${FRAME_DATA.length} frames — ${lang.toUpperCase()} / ${device.name}`}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", background: "#1a1a1a", borderRadius: 8, padding: 2 }}>
              {(["en","zh"] as Lang[]).map(l => (
                <button key={l} onClick={() => setLang(l)}
                  style={{ padding: "6px 16px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                    background: lang===l ? "#333" : "transparent", color: lang===l ? "#fff" : "#666" }}>
                  {l === "en" ? "EN" : "中文"}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", background: "#1a1a1a", borderRadius: 8, padding: 2 }}>
              {(["iphone","ipad"] as DeviceType[]).map(dt => (
                <button key={dt} onClick={() => setDeviceType(dt)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 16px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                    background: deviceType===dt ? "#333" : "transparent", color: deviceType===dt ? "#fff" : "#666" }}>
                  <Icon icon={dt==="iphone"?"lucide:smartphone":"lucide:tablet"} style={{ fontSize: 13 }} />
                  {dt==="iphone"?"iPhone":"iPad"}
                </button>
              ))}
            </div>
            {selectedIndex === null && (
              <select value={gridScale} onChange={e => setGridScale(parseFloat(e.target.value))}
                style={{ background: "#1a1a1a", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 13, color: "#fff", outline: "none" }}>
                {[0.08,0.1,0.12,0.15,0.2,0.25,0.3].map(v => <option key={v} value={v}>{Math.round(v*100)}%</option>)}
              </select>
            )}
            <span style={{ background: "#1a1a1a", borderRadius: 8, padding: "6px 12px", fontSize: 12, color: "#444" }}>
              {device.width}&times;{device.height}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1800, margin: "0 auto", padding: "32px 24px" }}>
        {selectedIndex !== null ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
            <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)" }}>
              <MinimalFrame
                s={expandedScale} d={deviceType} l={lang}
                img={getImage(selectedIndex)}
                onUp={(url: string) => setUploadedImages(p => ({ ...p, [imageKey(selectedIndex)]: url }))}
                data={FRAME_DATA[selectedIndex]}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button onClick={() => setSelectedIndex(selectedIndex > 0 ? selectedIndex-1 : null)}
                style={{ background: "#1a1a1a", color: "#888", padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer" }}>
                {selectedIndex > 0 ? "← Previous" : "Back"}
              </button>
              <span style={{ color: "#555", fontSize: 13 }}>{selectedIndex+1} / {FRAME_DATA.length}</span>
              {selectedIndex < FRAME_DATA.length-1 && (
                <button onClick={() => setSelectedIndex(selectedIndex+1)}
                  style={{ background: "#1a1a1a", color: "#888", padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer" }}>
                  Next →
                </button>
              )}
              {uploadedImages[imageKey(selectedIndex)] && (
                <button onClick={() => setUploadedImages(p => { const n={...p}; delete n[imageKey(selectedIndex)]; return n; })}
                  style={{ background: "rgba(220,38,38,0.1)", color: "#ef4444", padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer" }}>
                  Remove
                </button>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 24 }}>
            {FRAME_DATA.map((data, i) => (
              <div key={i} style={{ cursor: "pointer", transition: "transform 0.15s" }}
                onClick={() => setSelectedIndex(i)}
                onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.02)")}
                onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}>
                <div style={{ borderRadius: 12, overflow: "hidden", position: "relative",
                  boxShadow: "0 10px 30px -8px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.04)" }}>
                  <MinimalFrame s={gridScale} d={deviceType} l={lang}
                    img={getImage(i)}
                    onUp={(url: string) => setUploadedImages(p => ({ ...p, [imageKey(i)]: url }))}
                    data={data} />
                  {getImage(i) && (
                    <div style={{ position: "absolute", top: 8, right: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon icon="lucide:check" style={{ fontSize: 11, color: "#fff" }} />
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 12, textAlign: "center" }}>
                  <p style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>{i+1}. {lang === "zh" ? LABELS_ZH[i] : LABELS_EN[i]}</p>
                  <p style={{ color: "#555", fontSize: 11, marginTop: 2 }}>{getImage(i) ? "Ready" : "Click to upload"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppStoreScreenshots;
